import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Settings,
  CheckCircle,
  XCircle,
  Activity,
  Key,
  Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { mockThirdPartyProviders } from '@/api/mockData';

const ThirdPartyAPI = () => {
  const [providers, setProviders] = useState(mockThirdPartyProviders);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    apiUrl: '',
    apiKey: '',
    status: 'active' as 'active' | 'inactive'
  });

  const filteredProviders = providers.filter(provider =>
    provider.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreate = (e) => {
    e.preventDefault();
    const newProvider = {
      id: `3PL${Date.now()}`,
      ...formData,
      ordersCount: 0,
      successRate: 0
    };
    setProviders([...providers, newProvider]);
    setFormData({ name: '', apiUrl: '', apiKey: '', status: 'active' as 'active' | 'inactive' });
    setShowCreateDialog(false);
  };

  const handleEdit = (provider) => {
    setSelectedProvider(provider);
    setFormData({
      name: provider.name,
      apiUrl: provider.apiUrl,
      apiKey: provider.apiKey,
      status: provider.status
    });
    setShowEditDialog(true);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    setProviders(providers.map(p => 
      p.id === selectedProvider.id 
        ? { ...p, ...formData }
        : p
    ));
    setShowEditDialog(false);
    setSelectedProvider(null);
  };

  const handleDelete = (id) => {
    if (confirm('Bạn có chắc chắn muốn xóa nhà cung cấp này?')) {
      setProviders(providers.filter(p => p.id !== id));
    }
  };

  const toggleStatus = (id) => {
    setProviders(providers.map(p => 
      p.id === id 
        ? { ...p, status: p.status === 'active' ? 'inactive' : 'active' }
        : p
    ));
  };

  const testConnection = async (provider) => {
    // Mock API test
    alert(`Đang kiểm tra kết nối với ${provider.name}...`);
    setTimeout(() => {
      alert('Kết nối thành công!');
    }, 1000);
  };

  const getStatusColor = (status) => {
    return status === 'active' 
      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Quản lý API 3PL</h1>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90">
              <Plus className="h-4 w-4 mr-2" />
              Thêm 3PL
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Thêm nhà cung cấp 3PL mới</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Tên nhà cung cấp</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Vd: Giao Hàng Nhanh"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="status">Trạng thái</Label>
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background"
                  >
                    <option value="active">Hoạt động</option>
                    <option value="inactive">Tạm dừng</option>
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor="apiUrl">API URL</Label>
                <Input
                  id="apiUrl"
                  value={formData.apiUrl}
                  onChange={(e) => handleInputChange('apiUrl', e.target.value)}
                  placeholder="https://api.example.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="apiKey">API Key</Label>
                <Textarea
                  id="apiKey"
                  value={formData.apiKey}
                  onChange={(e) => handleInputChange('apiKey', e.target.value)}
                  placeholder="Nhập API key..."
                  rows={3}
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Hủy
                </Button>
                <Button type="submit">Thêm</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="animate-fade-in">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2 text-primary" />
              Danh sách nhà cung cấp 3PL
            </CardTitle>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
              <Input
                placeholder="Tìm kiếm nhà cung cấp..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredProviders.map((provider) => (
              <div
                key={provider.id}
                className="p-4 border rounded-lg hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      <Globe className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{provider.name}</h3>
                        <Badge className={getStatusColor(provider.status)}>
                          {provider.status === 'active' ? 'Hoạt động' : 'Tạm dừng'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{provider.apiUrl}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => testConnection(provider)}
                    >
                      <Activity className="h-4 w-4 mr-1" />
                      Test
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleEdit(provider)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Sửa
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => toggleStatus(provider.id)}
                    >
                      {provider.status === 'active' ? (
                        <XCircle className="h-4 w-4 mr-1" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-1" />
                      )}
                      {provider.status === 'active' ? 'Tắt' : 'Bật'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDelete(provider.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Xóa
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center">
                      <Key className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="font-medium">API Key</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {provider.apiKey.substring(0, 20)}...
                    </p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <span className="font-medium text-primary">
                      {provider.ordersCount} đơn hàng
                    </span>
                    <p className="text-xs text-muted-foreground">Đã xử lý</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <span className="font-medium text-green-600">
                      {provider.successRate}% thành công
                    </span>
                    <p className="text-xs text-muted-foreground">Tỷ lệ giao hàng</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Chỉnh sửa nhà cung cấp 3PL</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-name">Tên nhà cung cấp</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-status">Trạng thái</Label>
                <select
                  id="edit-status"
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background"
                >
                  <option value="active">Hoạt động</option>
                  <option value="inactive">Tạm dừng</option>
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="edit-apiUrl">API URL</Label>
              <Input
                id="edit-apiUrl"
                value={formData.apiUrl}
                onChange={(e) => handleInputChange('apiUrl', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-apiKey">API Key</Label>
              <Textarea
                id="edit-apiKey"
                value={formData.apiKey}
                onChange={(e) => handleInputChange('apiKey', e.target.value)}
                rows={3}
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Hủy
              </Button>
              <Button type="submit">Cập nhật</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ThirdPartyAPI;